import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, isValid } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { Activity, CircleDollarSign, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Malformed dates from legacy/CSV-imported records must never crash the render
// (date-fns format() THROWS RangeError on Invalid Date).
const safeDate = (raw) => {
  if (!raw) return '';
  try {
    const d = parseISO(raw);
    return isValid(d) ? format(d, 'MMM d') : '';
  } catch {
    return '';
  }
};

function Avatar({ src, name }) {
  if (src) {
    return <img src={src} alt={name} className="h-10 w-10 shrink-0 rounded-full object-cover" />;
  }
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#3B82F6] to-[#2563EB] text-xs font-bold text-white">
      {initials}
    </div>
  );
}

export const RecentActivity = () => {
  const { getAuthHeader } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/recent-activity?limit=8`, getAuthHeader());
      setEvents(res.data || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  return (
    <div
      className="rounded-[20px] bg-white p-6 shadow-[0px_18px_40px_rgba(112,144,176,0.12)]"
      data-testid="recent-activity-widget"
    >
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50">
          <Activity className="h-5 w-5 text-purple-600" strokeWidth={2} />
        </div>
        <h3 className="font-['Manrope'] text-lg font-bold text-[#2B3674]">Recent Activity</h3>
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-[#2447D6] border-t-transparent" />
        </div>
      ) : events.length === 0 ? (
        <div className="py-10 text-center" data-testid="activity-empty-state">
          <Activity className="mx-auto mb-2 h-10 w-10 text-[#E9EDF7]" />
          <p className="text-sm text-[#A3AED0]">No recent activity</p>
        </div>
      ) : (
        <div className="max-h-[360px] space-y-1 overflow-y-auto pr-1">
          {events.map((e, i) => (
            <div
              key={`${e.type}-${e.agent_name}-${e.date}-${i}`}
              className="flex items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-[#F4F7FE]"
              data-testid="activity-row"
            >
              <Avatar src={e.avatar} name={e.agent_name} />
              <div className="min-w-0 flex-1">
                {e.type === 'policy_issued' ? (
                  <p className="truncate text-[13px] leading-snug text-[#2B3674]">
                    <span className="font-bold">{e.agent_name}</span> issued a policy
                    {e.detail ? <> for <span className="font-semibold">{e.detail}</span></> : null}
                  </p>
                ) : (
                  <p className="truncate text-[13px] leading-snug text-[#2B3674]">
                    <span className="font-bold">{e.agent_name}</span> joined the team
                  </p>
                )}
                <p className="mt-0.5 text-xs text-[#A3AED0]">
                  {e.amount ? (
                    <span className="font-semibold text-[#01B574]">
                      ${Math.round(e.amount).toLocaleString()}
                    </span>
                  ) : null}
                  {e.amount && e.carrier ? ' · ' : ''}
                  {e.carrier || ''}
                  {(e.amount || e.carrier) && safeDate(e.date) ? ' · ' : ''}
                  {safeDate(e.date)}
                </p>
              </div>
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  e.type === 'policy_issued' ? 'bg-green-50' : 'bg-blue-50'
                }`}
              >
                {e.type === 'policy_issued' ? (
                  <CircleDollarSign className="h-4 w-4 text-[#01B574]" />
                ) : (
                  <UserPlus className="h-4 w-4 text-blue-600" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
